import { Component, OnInit, Output, Input, EventEmitter, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, SimpleChanges, OnChanges, inject } from '@angular/core';
import { ConsumerLoginConfig, CONSUMER_LOGIN_APP_CONFIG } from '../models/consumer-login-config';
import { SiteSettingService } from '../services/site-setting.service';
import { ConsumerLoginApiService } from '../services/consumer-login-api.service';
import { inputDefaults } from '../../input-config/input.defaults';
import { User, Verify, Auth, ApiRequest } from '../models';
import { BehaviorSubject, catchError, filter, finalize, map, Observable, of, Subscription, switchMap, take, tap, throwError } from 'rxjs';
import { Messages } from '../messages/strings';
import { TranslateService } from '@ngx-translate/core';
import isEmpty from 'lodash-es/isEmpty';
import { Language } from '@por/shared/core';
import { OrganizationVersionEnum } from '../enums/organization-version.enum';
import { RedirectionUsers } from '../models/redirection-users';
import { AuthService } from '../services/auth.service';
import { AppEventService } from '../services/app-event.service';
import { OriginActionName } from '@por/shared/ui/cross-app';
import { ActiveView } from '../enums/active-view.enum';
import { ErrorType, GenericError } from '../models/error.model';
import { ErrorService } from '../services/error.service';
import { AccountService } from '../services/accounts.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CreateCustomerResponse } from '../models/create-customer.interface';
import { PrivacyPolicyService } from '../services/privacy-policy.service';
import { PasswordResetResponse } from '@por/consumer-login/shared-lib';

@Component({
    standalone: false,
    selector: 'por-consumer-login',
    templateUrl: './consumer-login.component.html',
    styleUrls: ['./consumer-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConsumerLoginComponent implements OnInit, OnChanges, OnDestroy {
    private readonly siteSetting = inject(SiteSettingService);
    private readonly apiService = inject(ConsumerLoginApiService);
    private readonly appConfig = inject<ConsumerLoginConfig>(CONSUMER_LOGIN_APP_CONFIG);
    private readonly translateService = inject(TranslateService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly errorService = inject(ErrorService);
    private readonly authService = inject(AuthService);
    private readonly appEventService = inject(AppEventService);
    private readonly accountService = inject(AccountService);
    private readonly privacyPolicyService = inject(PrivacyPolicyService);

    /**
     * Always include the non-null assertion operator for the input variable(s).
     * We assume they are always populated, tsc isn't smart enough to know that.
     */

    @Output()
    readonly loginSuccess: EventEmitter<Auth> = new EventEmitter<Auth>();

    inputs: ConsumerLoginConfig | undefined;
    @Input() config: string | undefined;

    verifyTargetTemplate = 'signin';
    apiUrl!: string;
    orgId!: string;
    selectedTemplate = 'signin';
    isDisableButton = false;
    showAlert = false;
    showLoader = false;
    @Input() alertMessage = '';
    @Input() quickLinkUrl = '';
    @Output() readonly errorOutput: EventEmitter<GenericError> = new EventEmitter<GenericError>();
    userEmail!: string;
    userAccounts: RedirectionUsers[] = [];
    selectedContact: RedirectionUsers | undefined;
    private readonly demoBtnVisibilityCount = new BehaviorSubject<number>(0);
    demoBtnVisibilityCount$ = this.demoBtnVisibilityCount.asObservable();
    subscriptions: Subscription[] = [];
    errorMessage?: string;
    private readonly activeView = new BehaviorSubject(ActiveView.DefaultView);
    activeView$ = this.activeView.asObservable();
    isUserDisabled = false;

    constructor() {
        this.appEventService.initialize();
    }

    async ngOnInit() {
        this.translateService.use(Language.EN_GB);
        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config) as ConsumerLoginConfig;
            this.inputs = {
                ...this.inputs,
                logoUrl: this.inputs.logoUrl,
                faviconUrl: this.inputs.faviconUrl || inputDefaults.faviconUrl,
                isCustomerSelectionAllowed: this.inputs.isCustomerSelectionAllowed
            };
            this.privacyPolicyService.inputs = this.inputs;
        }
        this.validateInputConfig();
        this.subscriptions.push(
            this.errorService.error$.subscribe(result => {
                if (result) {
                    this.emitError(result);
                }
            })
        );

        if (this.inputs) this.siteSetting.setStylingForSite(this.inputs);
        await this.setData();

        if (this.alertMessage) {
            this.loadAlert(true, this.alertMessage);
        }
        const demoAppData = JSON.parse(localStorage.getItem('demoAppData') || '{}');
        if (!isEmpty(demoAppData)) {
            this.demoBtnVisibilityCount.next(5);
        }
        this.subscriptions.push(
            this.authService.getAuthData$
                .pipe(
                    filter((authData): authData is Auth => authData !== undefined && authData !== null && !!authData.accessToken),
                    switchMap(authData => this.handleAuthData(authData)),
                    catchError(error => {
                        this.handleErrors(error);
                        return of(null);
                    }),
                    finalize(() => this.loadLoader(false))
                )
                .subscribe()
        );

        this.subscriptions.push(
            this.authService.authDataError$.subscribe({
                next: error => {
                    if (error) {
                        this.handleErrors(error);
                        this.loadLoader(false);
                    }
                }
            })
        );
    }

    private handleAuthData(authData: Auth): Observable<void> {
        if (!this.inputs?.isCustomerSelectionAllowed) {
            this.loginSuccess.emit(authData);
            return of();
        }
        if (this.activeView.value === ActiveView.CustomerSelection) {
            return of();
        }
        return this.accountService.processUserAccounts(authData.Email, authData).pipe(
            tap(({ createCustomerResponse, userAccounts }) => {
                this.userAccounts = userAccounts ?? [];
                if (userAccounts?.length > 1) {
                    this.activeView.next(ActiveView.CustomerSelection);
                } else {
                    this.updateAuthData(undefined, createCustomerResponse, userAccounts);
                }
            }),
            catchError(error => {
                return throwError(() => error);
            }),
            finalize(() => this.loadLoader(false)),
            map(() => undefined)
        );
    }

    ngOnDestroy(): void {
        this.authService.resetAuthError();
        this.subscriptions.map((sub: Subscription) => sub.unsubscribe());
        this.activeView.next(ActiveView.DefaultView);
        this.loadAlert(false);
    }

    ngOnChanges(changes: SimpleChanges): void {
        Object.entries(changes).forEach(([key, value]) => {
            if (key === 'config' && value !== undefined) {
                /**
                 * Set input config which have been provided through when embeded index.html
                 */
                if (this.config && !isEmpty(this.config)) {
                    this.inputs = JSON.parse(this.config) as ConsumerLoginConfig;

                    // Set default values if not provided
                    this.inputs = {
                        ...this.inputs,
                        logoUrl: this.inputs.logoUrl || inputDefaults.logoUrl,
                        faviconUrl: this.inputs.faviconUrl || inputDefaults.faviconUrl,
                        isCustomerSelectionAllowed: this.inputs.isCustomerSelectionAllowed || inputDefaults.isCustomerSelectionAllowed
                    };
                    this.authService.initiateLogin(this.orgId, null);
                    this.setData();
                    this.siteSetting.setStylingForSite(this.inputs);
                }
                return;
            }
        });
    }

    // Set default values if not provided
    async setData() {
        this.orgId = this.inputs && !isEmpty(this.inputs) && !isEmpty(this.inputs?.organizationId) ? this.inputs.organizationId : inputDefaults.organizationId;
        const apiUrl =
            (!isEmpty(this.inputs) && !isEmpty(this.inputs?.loginApiUrl) ? this.inputs?.loginApiUrl : this.appConfig.loginApiUrl ? this.appConfig.loginApiUrl : inputDefaults.loginApiUrl) || '';
        const cpApiRoute: string =
            (!isEmpty(this.inputs) && !isEmpty(this.inputs?.consumerPortalApiUrl)
                ? this.inputs?.consumerPortalApiUrl
                : this.appConfig.consumerPortalApiUrl
                ? this.appConfig.consumerPortalApiUrl
                : inputDefaults.consumerPortalApiUrl) || '';
        this.apiService.setApiRoute(apiUrl, cpApiRoute);
    }

    activeTemplate(templateName: string) {
        this.selectedTemplate = templateName;
        this.loadLoader(false);

        this.loadAlert(false);
    }

    private emitError(error: GenericError) {
        this.errorOutput.emit(error);
    }

    handleLoginData(loginData: User): void {
        this.loadLoader(true);
        this.authService.initiateLogin(this.orgId, loginData);
    }

    setContact(contact: RedirectionUsers | undefined): void {
        this.updateAuthData(contact);
    }

    handleSignupData(signupData: User) {
        /* eslint-disable @typescript-eslint/naming-convention */
        /**
         * Note camelCase: DB model
         */
        this.loadLoader(true);
        this.verifyTargetTemplate = 'signin';
        const body: ApiRequest = {
            OrganizationId: this.orgId,
            Email: signupData.Email,
            Password: signupData.Password,
            StoreName: signupData.StoreName || this.inputs?.depotName,
            Name: signupData.Name,
            FirstName: signupData.FirstName,
            LastName: signupData.LastName,
            CompanyName: signupData.CompanyName,
            PhoneNumber: signupData.PhoneNumber,
            LogoUrl: this.inputs?.logoUrl || null,
            IsCustomerSelectionAllowed: this.inputs?.isCustomerSelectionAllowed ?? false
        };
        this.subscriptions.push(
            this.apiService
                .add('auth', 'signup', body)
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        this.userEmail = body.Email as string;
                        this.activeTemplate('verify');

                        this.loadAlert(true, Messages.accountCreated);
                    },
                    error: e => {
                        this.loadAlert(true, e.error?.message);
                        this.errorService.setError(ErrorType.AuthFailed);
                        this.loadLoader(false);
                    }
                })
        );
    }

    handleVerificationData(verificationData: Verify) {
        /* eslint-disable @typescript-eslint/naming-convention */
        /**
         * Note camelCase: DB model
         */
        this.loadLoader(true);
        const body: ApiRequest = {
            OrganizationId: this.orgId,
            Email: verificationData.email,
            VerificationCode: verificationData.code,
            LogoUrl: this.inputs?.logoUrl || null,
            StoreName: this.inputs?.storeName || this.inputs?.depotName
        };
        this.subscriptions.push(
            this.apiService
                .getByPost('auth', 'emails/verify', body)
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        this.userEmail = '';
                        this.activeTemplate(this.verifyTargetTemplate);

                        this.loadAlert(true, Messages.verificationCodeSuccess);
                    },
                    error: e => {
                        this.loadAlert(true, e.error?.message);
                        this.errorService.setError(ErrorType.AuthFailed);
                        this.loadLoader(false);
                    }
                })
        );
    }

    handleForgotPasswordData(forgotPasswordData: User) {
        /* eslint-disable @typescript-eslint/naming-convention */
        /**
         * Note camelCase: DB model
         */
        this.loadLoader(true);
        const body: ApiRequest = {
            OrganizationId: this.orgId,
            Email: forgotPasswordData.Email,
            StoreName: forgotPasswordData.StoreName || this.inputs?.depotName,
            LogoUrl: this.inputs?.logoUrl || null
        };

        this.subscriptions.push(
            this.apiService
                .getByPost('auth', 'passwords/forget', body)
                .pipe(take(1))
                .subscribe({
                    next: data => {
                        const response = data as PasswordResetResponse;
                        this.isUserDisabled = response.userDisabled;
                        this.userEmail = body.Email as string;
                        this.loadAlert(true, Messages.passwordResetCode);
                        this.activeTemplate('change-password');
                    },
                    error: () => {
                        this.errorService.setError(ErrorType.AuthFailed);
                        this.activeTemplate('change-password');
                    }
                })
        );
    }

    handleChangePasswordData(changePasswordData: User) {
        /* eslint-disable @typescript-eslint/naming-convention */
        /**
         * Note camelCase: DB model
         */
        if (changePasswordData?.Email) {
            this.loadLoader(true);
            const body: ApiRequest = {
                OrganizationId: this.orgId,
                Email: changePasswordData.Email,
                VerificationCode: changePasswordData.code,
                Password: changePasswordData.Password,
                LogoUrl: this.inputs?.logoUrl || null,
                StoreName: this.inputs?.storeName || this.inputs?.depotName
            };
            this.subscriptions.push(
                this.apiService
                    .getByPost('auth', 'passwords/reset', body)
                    .pipe(take(1))
                    .subscribe({
                        next: () => {
                            this.userEmail = '';
                            this.activeTemplate('signin');

                            this.loadAlert(true, Messages.passwordChanged);
                        },
                        error: e => {
                            this.loadAlert(true, e.error?.message);
                            this.errorService.setError(ErrorType.AuthFailed);
                            this.loadLoader(false);
                        }
                    })
            );
        }
    }

    loadLoader(isShow: boolean): void {
        this.showLoader = isShow;
        this.isDisableButton = isShow;
        this.cdr.detectChanges();
    }

    loadAlert(isShow: boolean, message = ''): void {
        this.showAlert = isShow;
        this.alertMessage = message;
        this.cdr.detectChanges();
    }

    onLogoClick(): void {
        if (this.inputs?.logoRedirectUrl) {
            window.location.href = this.inputs.logoRedirectUrl;
            return;
        }
        this.showDemoApp();
    }

    showDemoApp(): void {
        if (this.appConfig.production === true) {
            return;
        }
        let value: number = this.demoBtnVisibilityCount.value;
        this.demoBtnVisibilityCount.next(++value);
    }

    showAdvanceNotation(): boolean {
        return this.inputs?.license === OrganizationVersionEnum.Advanced;
    }

    private handleErrors(error: HttpErrorResponse | null) {
        const errorMessage = error?.status === 0 ? this.translateService.instant('NetworkError') : error?.error?.message || 'An error occurred';
        this.errorService.setError(ErrorType.AuthFailed);
        this.loadAlert(true, errorMessage);
    }

    /**
     * Make sure orgId exist and have valid data
     */
    private validateInputConfig() {
        if (this.inputs?.organizationId === 'undefined' || !this.inputs?.organizationId) {
            this.errorService.setError(ErrorType.InvalidSubDomainOrgId);
            this.activeTemplate('configError');
        }
    }

    private updateAuthData(contact?: RedirectionUsers, createCustomerResponse?: CreateCustomerResponse | null, userAccounts?: RedirectionUsers[]): void {
        let updatedAuthData = this.authService.getValue();
        if (!updatedAuthData) return;

        if (createCustomerResponse) {
            updatedAuthData = {
                ...updatedAuthData,
                /* eslint-disable @typescript-eslint/naming-convention */
                CompanyName: createCustomerResponse.customerName,
                customerId: createCustomerResponse.customerId,
                contactId: createCustomerResponse.contactId
            };
        } else if (contact) {
            updatedAuthData = {
                ...updatedAuthData,
                customerId: contact.customerId,
                contactId: contact.contactId,
                CompanyName: contact.customerName ?? updatedAuthData.CompanyName
            };
        } else if (userAccounts && userAccounts.length === 1) {
            updatedAuthData = {
                ...updatedAuthData,
                customerId: userAccounts[0].customerId,
                contactId: userAccounts[0].contactId,
                CompanyName: userAccounts[0].customerName
            };
        }

        this.appEventService.dispatch({
            actionName: OriginActionName.CustomerSelected,
            data: updatedAuthData
        });
        this.loginSuccess.emit(updatedAuthData);
    }

    isLogoDisplayed(): boolean {
        // Don't show logo if storeName matches organizationId (show depot name instead)
        const storeName = this.inputs?.storeName;
        const organizationId = this.inputs?.organizationId;

        if (storeName && organizationId && storeName === organizationId) {
            return false; // Show depot name instead
        }

        // Show logo if it exists
        return this.inputs?.logoUrl !== undefined && this.inputs?.logoUrl !== null && this.inputs?.logoUrl !== '';
    }
}
